import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

// Reward renderer registry — new kinds (earned in later years) plug in here
// with zero changes to the store or the world screen.

const COLORS = ["#FF7A45", "#EF476F", "#118AB2", "#9B5DE5", "#06D6A0", "#F4A261"];

function Flower({ color, className }) {
  return (
    <svg viewBox="0 0 60 96" className={className}>
      <path d="M30 92 L30 44" stroke="#4DBC85" strokeWidth="5" strokeLinecap="round" />
      <ellipse cx="20" cy="68" rx="9" ry="5" transform="rotate(-32 20 68)" fill="#57C690" />
      <ellipse cx="40" cy="76" rx="9" ry="5" transform="rotate(32 40 76)" fill="#57C690" />
      {[0, 60, 120, 180, 240, 300].map((a) => (
        <ellipse key={a} cx="30" cy="11" rx="8" ry="13" fill={color} transform={`rotate(${a} 30 26)`} />
      ))}
      <circle cx="30" cy="26" r="10" fill="#FFD166" />
      <circle cx="26.5" cy="24" r="1.6" fill="#2B2D42" />
      <circle cx="33.5" cy="24" r="1.6" fill="#2B2D42" />
      <path d="M26 29 Q30 32.5 34 29" stroke="#2B2D42" strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function Butterfly({ color, className }) {
  return (
    <svg viewBox="0 0 60 52" className={className}>
      <ellipse cx="18" cy="18" rx="14" ry="12" fill={color} opacity="0.9" />
      <ellipse cx="42" cy="18" rx="14" ry="12" fill={color} opacity="0.9" />
      <ellipse cx="21" cy="36" rx="10" ry="8" fill="#FFD166" />
      <ellipse cx="39" cy="36" rx="10" ry="8" fill="#FFD166" />
      <rect x="27" y="12" width="6" height="32" rx="3" fill="#2B2D42" />
      <path d="M28 12 Q24 2 18 4 M32 12 Q36 2 42 4" stroke="#2B2D42" strokeWidth="2" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function Bird({ color, className }) {
  return (
    <svg viewBox="0 0 60 56" className={className}>
      <circle cx="30" cy="30" r="20" fill={color} />
      <path d="M14 30 Q26 22 30 36 Q22 42 14 30 Z" fill="#FFFFFF" opacity="0.35" />
      <circle cx="38" cy="24" r="2.6" fill="#2B2D42" />
      <path d="M48 28 L58 31 L48 34 Z" fill="#FFD166" />
      <path d="M24 50 L24 56 M36 50 L36 56" stroke="#E8920C" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function LittleTree({ color, className }) {
  return (
    <svg viewBox="0 0 60 88" className={className}>
      <rect x="26" y="52" width="8" height="34" rx="4" fill="#A9746E" />
      <circle cx="30" cy="34" r="24" fill={color} />
      <circle cx="16" cy="44" r="12" fill={color} opacity="0.85" />
      <circle cx="44" cy="44" r="12" fill={color} opacity="0.85" />
    </svg>
  );
}

const KIND_RENDERERS = { flower: Flower, butterfly: Butterfly, bird: Bird, tree: LittleTree };

const SLOTS = [
  { left: "30%", bottom: "3%" },
  { left: "43%", bottom: "8%" },
  { left: "56%", bottom: "2.5%" },
  { left: "67%", bottom: "6%" },
  { left: "36%", bottom: "14%" },
  { left: "61%", bottom: "13%" },
  { left: "48%", bottom: "17%" },
  { left: "26%", bottom: "9%" },
  { left: "71%", bottom: "16%" },
  { left: "51%", bottom: "9%" },
];

function Sparkles() {
  return (
    <motion.span initial={{ opacity: 1 }} animate={{ opacity: 0 }} transition={{ duration: 1 }} className="pointer-events-none absolute -inset-2" aria-hidden>
      {[[-6, -8], [26, -12], [40, 8], [-10, 18], [18, -22]].map(([x, y], i) => (
        <motion.span
          key={i}
          className="absolute left-1/2 top-1/3 h-1.5 w-1.5 rounded-full bg-secondary"
          initial={{ x: 0, y: 0, scale: 0 }}
          animate={{ x, y, scale: [0, 1.4, 0] }}
          transition={{ duration: 0.9, delay: i * 0.05 }}
        />
      ))}
    </motion.span>
  );
}

export function LivingElements({ elements, onTap, reduce }) {
  const [sparkleId, setSparkleId] = useState(null);
  return (
    <>
      {elements.map((el, i) => {
        const Renderer = KIND_RENDERERS[el.kind] || Flower;
        const slot = SLOTS[i % SLOTS.length];
        return (
          <motion.button
            key={el.id}
            data-testid={`living-element-${i}`}
            aria-label="A living thing you grew — tap to hear its story"
            onClick={() => {
              setSparkleId(el.id);
              onTap(el);
            }}
            className={cn("absolute z-20 origin-bottom focus-visible:outline-none")}
            style={{ left: slot.left, bottom: slot.bottom }}
            initial={{ scale: 0, y: 12 }}
            animate={reduce ? { scale: 1, y: 0 } : { scale: 1, y: 0, rotate: [0, i % 2 ? 2.5 : -2.5, 0] }}
            transition={{
              scale: { type: "spring", stiffness: 260, damping: 16, delay: 0.15 + i * 0.08 },
              rotate: { duration: 3 + (i % 3), repeat: Infinity, ease: "easeInOut", delay: i * 0.4 },
            }}
            whileTap={{ scale: 1.15 }}
          >
            <Renderer color={COLORS[i % COLORS.length]} className="h-12 w-auto drop-shadow-sm sm:h-16" />
            <AnimatePresence>{sparkleId === el.id && <Sparkles key={`s-${el.id}-${i}`} />}</AnimatePresence>
          </motion.button>
        );
      })}
    </>
  );
}
